import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SidebarContext } from "../../AppRoutes";
import { AuthContext } from "../../contexts/auth";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import { getTeam, inviteUser, updateUserRole, updateUserModules, deletePermanentUser } from "../../services/api";
import PermissionGate from "../../services/PermissionGate";
import ConfirmDialog from "../../components/ConfirmDialog";

import { ROLE_LABELS, getInvitableRoles, canManageRole, VALID_MODULES, CARGO_MODULE_PRESETS } from "../../constants/permissions";

import PhoneInput from '../../components/PhoneInput';
import { fromE164 } from '../../utils/phone';
import { FiUsers, FiUserPlus, FiEdit2, FiTrash2, FiUser, FiSearch, FiRefreshCw, FiMail, FiShield, FiX, FiSettings, FiPhone } from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

// ==============================
// STYLED COMPONENTS
// ==============================
const PageContainer = styled.div`
    display: flex;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: white; //linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%);
    overflow: initial;
    z-index: 9;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    //max-width: 1200px;
    margin: 0 auto;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;

    .team-page-title {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 4px;
    }

    .team-page-title h1 {
        font-size: 28px;
        font-weight: 700;
        color: #1a202c;
        margin: 0;
    }

    .team-page-title .team-icon {
        font-size: 28px;
        color: rgb(49, 140, 213);
    }

    .team-page-subtitle {
        color: #718096;
        font-size: 14px;
        margin: 0;
    }
`;

const ControlsBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 16px;
    flex-wrap: wrap;

    .team-search-box {
        position: relative;
        flex: 1;
        max-width: 400px;
        min-width: 200px;
    }

    .team-search-box input {
        width: 100%;
        padding: 10px 14px 10px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        font-size: 14px;
        background: #fff;
        transition: all 0.2s ease;
        outline: none;
    }

    .team-search-box input:focus {
        border-color: rgb(49, 140, 213);
        box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.15);
    }

    .team-search-box .team-search-icon {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #a0aec0;
        font-size: 16px;
    }

    .team-controls-right {
        display: flex;
        gap: 10px;
        align-items: center;
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;

        .team-search-box {
            max-width: 100%;
        }

        .team-controls-right {
            justify-content: stretch;
        }
    }
`;

const RefreshButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    color: #4a5568;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: rgb(49, 140, 213);
        color: rgb(49, 140, 213);
        background: rgba(49, 140, 213, 0.04);
    }

    svg {
        font-size: 16px;
    }
`;

const InviteButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(49, 140, 213, 0.3);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(49, 140, 213, 0.4);
    }

    &:active {
        transform: translateY(0);
    }

    svg {
        font-size: 18px;
    }
`;

const TeamCard = styled.div`
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
`;

const TeamTable = styled.div`
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    table {
        width: 100%;
        border-collapse: collapse;
        min-width: 650px;
    }

    thead {
        background: #f7fafc;
        border-bottom: 2px solid #edf2f7;
    }

    thead th {
        padding: 14px 20px;
        font-size: 12px;
        font-weight: 600;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: left;
        white-space: nowrap;
    }

    tbody tr {
        border-bottom: 1px solid #edf2f7;
        transition: background 0.15s ease;
    }

    tbody tr:hover {
        background: #f7fafc;
    }

    tbody tr:last-child {
        border-bottom: none;
    }

    tbody td {
        padding: 16px 20px;
        font-size: 14px;
        color: #2d3748;
        vertical-align: middle;
    }

    .team-member-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .team-member-avatar {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        color: #fff;
        text-transform: uppercase;
        flex-shrink: 0;
    }

    .team-member-name {
        font-weight: 600;
        color: #1a202c;
        line-height: 1.3;
    }

    .team-member-email {
        font-size: 13px;
        color: #a0aec0;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .team-member-email svg {
        font-size: 12px;
    }

    .team-role-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
    }

    .team-role-badge svg {
        font-size: 12px;
    }

    .team-you-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
        background: rgba(49, 140, 213, 0.1);
        color: rgb(49, 140, 213);
        margin-left: 8px;
    }

    .team-date-cell {
        color: #718096;
        font-size: 13px;
        white-space: nowrap;
    }

    .team-actions-cell {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .team-action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        background: #fff;
        cursor: pointer;
        transition: all 0.2s ease;
        color: #718096;
    }

    .team-action-btn:hover {
        border-color: rgb(49, 140, 213);
        color: rgb(49, 140, 213);
        background: rgba(49, 140, 213, 0.04);
    }

    .team-action-btn.team-action-danger:hover {
        border-color: #e53e3e;
        color: #e53e3e;
        background: rgba(229, 62, 62, 0.04);
    }

    .team-action-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        pointer-events: none;
    }

    .team-action-btn svg {
        font-size: 16px;
    }

    @media (max-width: 600px) {
        table {
            min-width: 500px;
        }
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    text-align: center;

    .team-empty-icon {
        font-size: 56px;
        color: #cbd5e0;
        margin-bottom: 16px;
    }

    .team-empty-text {
        font-size: 16px;
        color: #718096;
        max-width: 360px;
        line-height: 1.6;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;

    svg {
        font-size: 48px;
        color: rgb(49, 140, 213);
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

const MemberCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    background: rgba(49, 140, 213, 0.1);
    color: rgb(49, 140, 213);
    margin-left: 8px;
`;

// ==============================
// MODAL STYLED COMPONENTS
// ==============================
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 24px;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    animation: slideUp 0.3s ease;
    max-height: 90vh;
    overflow-y: auto;

    @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }

    .team-modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px;
        border-bottom: 1px solid #edf2f7;
    }

    .team-modal-header h2 {
        font-size: 20px;
        font-weight: 700;
        color: #1a202c;
        margin: 0;
    }

    .team-modal-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 8px;
        background: #f7fafc;
        cursor: pointer;
        color: #718096;
        transition: all 0.2s ease;
    }

    .team-modal-close:hover {
        background: #edf2f7;
        color: #1a202c;
    }

    .team-modal-body {
        padding: 24px;
    }

    .team-modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 16px 24px;
        border-top: 1px solid #edf2f7;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 18px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #4a5568;
        margin-bottom: 6px;
    }

    input, select {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        font-size: 14px;
        color: #1a202c;
        outline: none;
        transition: all 0.2s ease;
        background: #fff;
    }

    input:focus, select:focus {
        border-color: rgb(49, 140, 213);
        box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.15);
    }

    input::placeholder {
        color: #cbd5e0;
    }

    select {
        appearance: none;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
        background-position: right 12px center;
        background-repeat: no-repeat;
        background-size: 16px 16px;
        padding-right: 36px;
    }

    .team-form-error {
        font-size: 12px;
        color: #e53e3e;
        margin-top: 4px;
    }
`;

const ModalCancelButton = styled.button`
    padding: 10px 20px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    color: #4a5568;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #f7fafc;
        border-color: #cbd5e0;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ModalSubmitButton = styled.button`
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(49, 140, 213, 0.3);
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(49, 140, 213, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }

    .team-btn-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }
`;

// ==============================
// ROLE EDIT SELECT (inline dropdown)
// ==============================
const RoleSelectInline = styled.select`
    padding: 4px 28px 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    border: 2px solid transparent;
    cursor: pointer;
    outline: none;
    transition: all 0.2s ease;
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 8px center;
    background-repeat: no-repeat;
    background-size: 12px 12px;

    &:focus {
        border-color: rgb(49, 140, 213);
        box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.15);
    }
`;


// ==============================
// MODULES GRID STYLED COMPONENT
// ==============================
const ModulesGrid = styled.div`
    margin-top: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;

    .modules-grid-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background: #f7fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    .modules-grid-title {
        font-size: 13px;
        font-weight: 600;
        color: #4a5568;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .modules-grid-levels {
        display: flex;
        gap: 16px;
    }

    .modules-grid-levels span {
        font-size: 11px;
        font-weight: 600;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        width: 60px;
        text-align: center;
    }

    .modules-grid-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 16px;
        border-bottom: 1px solid #f1f5f9;
        transition: background 0.15s ease;
    }

    .modules-grid-row:last-child {
        border-bottom: none;
    }

    .modules-grid-row:hover {
        background: #f7fafc;
    }

    .modules-grid-module-name {
        font-size: 13px;
        color: #2d3748;
        font-weight: 500;
        flex: 1;
    }

    .modules-grid-checkboxes {
        display: flex;
        gap: 16px;
    }

    .modules-grid-checkbox-wrapper {
        width: 60px;
        display: flex;
        justify-content: center;
    }

    .modules-grid-checkbox {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: rgb(49, 140, 213);
    }

    .modules-grid-bypass {
        padding: 20px 16px;
        text-align: center;
        color: #718096;
        font-size: 13px;
        font-style: italic;
    }

    .modules-grid-count {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
        background: rgba(49, 140, 213, 0.1);
        color: rgb(49, 140, 213);
    }
`;

// ==============================
// COMPONENT
// ==============================
const TeamManagement = ({ fixedRoleFilter = null }) => {
    const { sidebar } = useContext(SidebarContext);
    const { user, userHasPermission } = useContext(AuthContext);
    const { t, i18n } = useTranslation('team');

    // State
    const [team, setTeam] = useState([]);
    const [invitableRoles, setInvitableRoles] = useState([]);
    const [filter, setFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [refreshFlag, setRefreshFlag] = useState(false);

    // Invite modal
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteForm, setInviteForm] = useState({ name: '', email: '', password: '', role: '', number: '' });
    const [inviteErrors, setInviteErrors] = useState({});

    // Delete confirm
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Modules state for invite
    const [inviteModules, setInviteModules] = useState([]);

    // Modules edit modal
    const [showModulesModal, setShowModulesModal] = useState(false);
    const [modulesEditTarget, setModulesEditTarget] = useState(null);
    const [editModules, setEditModules] = useState([]);
    const [modulesLoading, setModulesLoading] = useState(false);

    // Current user role
    const currentRole = user?.role || 'caixa';

    // Computed invitable roles for current user
    const availableRoles = useMemo(() => {
        return invitableRoles.length > 0 ? invitableRoles : getInvitableRoles(currentRole);
    }, [invitableRoles, currentRole]);

    // ==============================
    // FETCH TEAM
    // ==============================
    const fetchTeam = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getTeam();
            const data = response.data;
            setTeam(data.users || []);
            if (data.invitableRoles) {
                setInvitableRoles(data.invitableRoles);
            }
        } catch (error) {
            console.error('[TEAM] Erro ao carregar equipe:', error);
            toast.error(t('invite.error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchTeam();
    }, [fetchTeam, refreshFlag]);

    // ==============================
    // FILTER
    // ==============================
    const filteredTeam = useMemo(() => {
        let result = team;

        // Filtro fixo por role (ex: quando acessa via /mesas-garcons)
        if (fixedRoleFilter) {
            result = result.filter(member => member.role === fixedRoleFilter);
        }

        // Filtro de busca por texto
        if (filter) {
            const exp = new RegExp(filter.replace(/[^\d\w]+/g, '.*'), 'i');
            result = result.filter(member =>
                exp.test(member.name) ||
                exp.test(member.email) ||
                exp.test(member.role)
            );
        }

        return result;
    }, [filter, team, fixedRoleFilter]);

    // ==============================
    // INVITE MEMBER
    // ==============================
    const validateInviteForm = () => {
        const errors = {};
        if (!inviteForm.name.trim()) errors.name = true;
        if (!inviteForm.email.trim() || !/\S+@\S+\.\S+/.test(inviteForm.email)) errors.email = true;
        if (!inviteForm.password || inviteForm.password.length < 6) errors.password = true;
        if (!inviteForm.role) errors.role = true;
        setInviteErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInviteSubmit = async () => {
        if (!validateInviteForm()) return;

        setInviteLoading(true);
        try {
            await inviteUser({
                name: inviteForm.name.trim(),
                email: inviteForm.email.trim().toLowerCase(),
                password: inviteForm.password,
                role: inviteForm.role,
                modules: inviteModules,
                number: inviteForm.number ? fromE164(inviteForm.number) : undefined,
            });
            toast.success(t('invite.success'));
            setShowInviteModal(false);
            setInviteForm({ name: '', email: '', password: '', role: '', number: '' });
            setInviteModules([]);
            setInviteErrors({});
            setRefreshFlag(prev => !prev);
        } catch (error) {
            const msg = error?.response?.data?.msg || t('invite.error');
            toast.error(msg);
        } finally {
            setInviteLoading(false);
        }
    };

    const openInviteModal = () => {
        const preRole = fixedRoleFilter || '';
        setInviteForm({ name: '', email: '', password: '', role: preRole, number: '' });
        setInviteModules(preRole ? initModulesFromPreset(preRole) : []);
        setInviteErrors({});
        setShowInviteModal(true);
    };

    // ==============================
    // DELETE MEMBER (permanent)
    // ==============================
    const openDeleteConfirm = (member) => {
        setMemberToDelete(member);
        setConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!memberToDelete) return;

        setDeleteLoading(true);
        try {
            await deletePermanentUser(memberToDelete._id);
            toast.success(t('actions.deleteSuccess', { defaultValue: 'Member permanently removed!' }));
            setConfirmOpen(false);
            setMemberToDelete(null);
            setRefreshFlag(prev => !prev);
        } catch (error) {
            const msg = error?.response?.data?.msg || t('invite.error');
            toast.error(msg);
        } finally {
            setDeleteLoading(false);
        }
    };

    // ==============================
    // HELPERS
    // ==============================
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return parts[0].substring(0, 2).toUpperCase();
    };

    const getRoleColor = (role) => {
        return ROLE_LABELS[role]?.color || '#718096';
    };

    // Safely extract image URL (user_img can be string, array, or empty array)
    const getUserImage = (userImg) => {
        if (!userImg) return null;
        if (Array.isArray(userImg)) return userImg[0] || null;
        if (typeof userImg === 'string' && userImg.trim()) return userImg;
        return null;
    };

    const getRoleLabel = (role) => {
        return t(`roles.${role}`, role);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString(i18n.language, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch {
            return '-';
        }
    };

    const isCurrentUser = (member) => {
        return member._id === user?._id || member._id === user?.id;
    };

    const canEditMember = (member) => {
        if (isCurrentUser(member)) return false;
        return canManageRole(currentRole, member.role);
    };

    // ==============================
    // MODULES HELPERS
    // ==============================

    // Initialize modules array from a preset or existing modules
    const initModulesFromPreset = (role) => {
        if (['owner', 'empresa'].includes(role)) return [];
        const preset = CARGO_MODULE_PRESETS[role];
        if (preset) return preset.map(m => ({ ...m }));
        return [];
    };

    // Handle checkbox change with cascading logic
    const handleModuleCheckbox = (modules, setModulesFn, moduleName, level, checked) => {
        setModulesFn(prev => {
            const updated = [...prev];
            let mod = updated.find(m => m.module === moduleName);

            if (!mod) {
                mod = { module: moduleName, read: false, write: false, delete: false };
                updated.push(mod);
            }

            if (level === 'delete') {
                mod.delete = checked;
                if (checked) { mod.write = true; mod.read = true; } // Cascade up
            } else if (level === 'write') {
                mod.write = checked;
                if (checked) { mod.read = true; } // Cascade up
                if (!checked) { mod.delete = false; } // Cascade down
            } else if (level === 'read') {
                mod.read = checked;
                if (!checked) { mod.write = false; mod.delete = false; } // Cascade down
            }

            // Remove module entry if all permissions are false
            return updated.filter(m => m.read || m.write || m.delete);
        });
    };

    // Get a specific level value from modules array
    const getModuleLevel = (modules, moduleName, level) => {
        const mod = modules.find(m => m.module === moduleName);
        if (!mod) return false;
        return !!mod[level];
    };

    // When invite role changes, auto-fill modules from preset
    const handleInviteRoleChange = (role) => {
        setInviteForm(prev => ({ ...prev, role }));
        setInviteModules(initModulesFromPreset(role));
    };

    // Open unified edit modal (role + modules) for a member
    const openEditMember = (member) => {
        setModulesEditTarget(member);
        setEditModules(member.modules ? member.modules.map(m => ({ ...m })) : initModulesFromPreset(member.role));
        setEditRole(member.role);
        setShowModulesModal(true);
    };

    // Role being edited in the unified modal
    const [editRole, setEditRole] = useState('');

    // Handle role change inside edit modal - auto-update modules preset
    const handleEditRoleChange = (newRole) => {
        setEditRole(newRole);
        if (!['owner', 'empresa'].includes(newRole)) {
            setEditModules(initModulesFromPreset(newRole));
        } else {
            setEditModules([]);
        }
    };

    // Save role + modules together
    const handleSaveEdit = async () => {
        if (!modulesEditTarget) return;
        setModulesLoading(true);
        try {
            // Update role if changed
            if (editRole !== modulesEditTarget.role) {
                await updateUserRole(modulesEditTarget._id, editRole);
            }
            // Update modules if not bypass role
            if (!['owner', 'empresa', 'Admin'].includes(editRole)) {
                await updateUserModules(modulesEditTarget._id, editModules);
            }
            toast.success(t('modules.modulesUpdated', { defaultValue: 'Member updated successfully!' }));
            setShowModulesModal(false);
            setModulesEditTarget(null);
            setRefreshFlag(prev => !prev);
        } catch (error) {
            const msg = error?.response?.data?.msg || t('modules.modulesError');
            toast.error(msg);
        } finally {
            setModulesLoading(false);
        }
    };

    // Count active modules for a member
    const getActiveModulesCount = (member) => {
        if (['owner', 'empresa', 'Admin'].includes(member.role)) return -1; // bypass
        return (member.modules || []).filter(m => m.read || m.write || m.delete).length;
    };

    // ==============================
    // RENDER
    // ==============================
    return (
        <PermissionGate permissions={['default']}>
            <PageContainer sidebar={sidebar}>
                <ContentWrapper>
                    {/* Header */}
                    <PageHeader>
                        <div className="page-title-with-subtitle">
                            <h1>{fixedRoleFilter ? t(`roles.${fixedRoleFilter}`, fixedRoleFilter) + 's' : t('pageTitle')}</h1>
                            <p className="page-subtitle">
                                {fixedRoleFilter
                                    ? t('subtitleFiltered', { role: t(`roles.${fixedRoleFilter}`, fixedRoleFilter) })
                                    : t('subtitle')
                                }
                            </p>
                        </div>
                    </PageHeader>

                    {/* Controls */}
                    <ControlsBar>
                        <div className="team-search-box">
                            <FiSearch className="team-search-icon" />
                            <input
                                type="text"
                                placeholder={t('table.name') + ', ' + t('table.email') + '...'}
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                        </div>

                        <div className="team-controls-right">
                            <RefreshButton onClick={() => { setLoading(true); setRefreshFlag(prev => !prev); }}>
                                <FiRefreshCw /> {t('refreshButton')}
                            </RefreshButton>

                            {(userHasPermission('users:invite') || currentRole === 'Admin') && (
                                <InviteButton onClick={openInviteModal}>
                                    <FiUserPlus /> {t('inviteButton')}
                                </InviteButton>
                            )}
                        </div>
                    </ControlsBar>

                    {/* Table Card */}
                    <TeamCard>
                        {loading ? (
                            <LoadingContainer>
                                <AiOutlineLoading3Quarters />
                            </LoadingContainer>
                        ) : filteredTeam.length === 0 ? (
                            <EmptyState>
                                <FiUsers className="team-empty-icon" />
                                <p className="team-empty-text">
                                    {filter
                                        ? `${t('emptyState').split('.')[0]}.`
                                        : fixedRoleFilter
                                            ? t('emptyStateFiltered', { role: t(`roles.${fixedRoleFilter}`, fixedRoleFilter) })
                                            : t('emptyState')
                                    }
                                </p>
                            </EmptyState>
                        ) : (
                            <TeamTable>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('table.name')}</th>
                                            <th>{t('table.role')}</th>
                                            <th>{t('table.createdAt')}</th>
                                            <th>{t('table.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTeam.map((member) => {
                                            const roleColor = getRoleColor(member.role);
                                            const isSelf = isCurrentUser(member);
                                            const editable = canEditMember(member);

                                            return (
                                                <tr key={member._id}>
                                                    {/* Name + Email */}
                                                    <td>
                                                        <div className="team-member-info">
                                                            <div
                                                                className="team-member-avatar"
                                                                style={{ background: getUserImage(member.user_img) ? 'transparent' : `${roleColor}18` }}
                                                            >
                                                                {getUserImage(member.user_img) ? (
                                                                    <img
                                                                        src={getUserImage(member.user_img)}
                                                                        alt={member.name}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            borderRadius: '50%',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <FiUser style={{ fontSize: 18, color: roleColor }} />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <div className="team-member-name">
                                                                    {member.name}
                                                                    {isSelf && (
                                                                        <span className="team-you-badge">
                                                                            {t('badges.you')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="team-member-email">
                                                                    <FiMail /> {member.email}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Role */}
                                                    <td>
                                                        <span
                                                            className="team-role-badge"
                                                            style={{
                                                                background: `${roleColor}15`,
                                                                color: roleColor,
                                                            }}
                                                        >
                                                            <FiShield /> {getRoleLabel(member.role)}
                                                        </span>
                                                        {(() => {
                                                            const count = getActiveModulesCount(member);
                                                            if (count === -1) return null;
                                                            return (
                                                                <span style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '3px',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '10px',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    background: count > 0 ? 'rgba(49, 140, 213, 0.1)' : 'rgba(229, 62, 62, 0.1)',
                                                                    color: count > 0 ? 'rgb(49, 140, 213)' : '#e53e3e',
                                                                    marginLeft: '6px',
                                                                }}>
                                                                    <FiSettings style={{ fontSize: 10 }} />
                                                                    {count}/{VALID_MODULES.length}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>

                                                    {/* Created At */}
                                                    <td>
                                                        <span className="team-date-cell">
                                                            {formatDate(member.createdAt)}
                                                        </span>
                                                    </td>

                                                    {/* Actions */}
                                                    <td>
                                                        <div className="team-actions-cell">
                                                            <button
                                                                className="team-action-btn"
                                                                title={t('actions.editRole', { defaultValue: 'Edit' })}
                                                                disabled={!editable}
                                                                onClick={() => openEditMember(member)}
                                                            >
                                                                <FiEdit2 />
                                                            </button>
                                                            <button
                                                                className="team-action-btn team-action-danger"
                                                                title={t('actions.delete', { defaultValue: 'Delete' })}
                                                                disabled={!editable}
                                                                onClick={() => openDeleteConfirm(member)}
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </TeamTable>
                        )}
                    </TeamCard>
                </ContentWrapper>
            </PageContainer>

            {/* ========== INVITE MODAL ========== */}
            {showInviteModal && (
                <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) setShowInviteModal(false); }}>
                    <ModalContent>
                        <div className="team-modal-header">
                            <h2>{t('invite.title')}</h2>
                            <button className="team-modal-close" onClick={() => setShowInviteModal(false)}>
                                <FiX />
                            </button>
                        </div>

                        <div className="team-modal-body">
                            <FormGroup>
                                <label>{t('invite.name')}</label>
                                <input
                                    type="text"
                                    placeholder={t('invite.namePlaceholder')}
                                    value={inviteForm.name}
                                    onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                                    style={inviteErrors.name ? { borderColor: '#e53e3e' } : {}}
                                    autoFocus
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{t('invite.email')}</label>
                                <input
                                    type="email"
                                    placeholder={t('invite.emailPlaceholder')}
                                    value={inviteForm.email}
                                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                                    style={inviteErrors.email ? { borderColor: '#e53e3e' } : {}}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{t('invite.number')}</label>
                                <PhoneInput
                                    value={inviteForm.number}
                                    onChange={(val) => setInviteForm(prev => ({ ...prev, number: val || '' }))}
                                    placeholder={t('invite.numberPlaceholder')}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{t('invite.password')}</label>
                                <input
                                    type="text"
                                    placeholder={t('invite.passwordPlaceholder')}
                                    value={inviteForm.password}
                                    onChange={(e) => setInviteForm(prev => ({ ...prev, password: e.target.value }))}
                                    style={inviteErrors.password ? { borderColor: '#e53e3e' } : {}}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{t('invite.role')}</label>
                                <select
                                    value={inviteForm.role}
                                    onChange={(e) => handleInviteRoleChange(e.target.value)}
                                    disabled={!!fixedRoleFilter}
                                    style={{
                                        ...(inviteErrors.role ? { borderColor: '#e53e3e' } : {}),
                                        ...(fixedRoleFilter ? { opacity: 0.7, cursor: 'not-allowed' } : {})
                                    }}
                                >
                                    <option value="">{t('invite.rolePlaceholder')}</option>
                                    {availableRoles.map(role => (
                                        <option key={role} value={role}>
                                            {getRoleLabel(role)}
                                        </option>
                                    ))}
                                </select>
                            </FormGroup>

                            {/* Modules Grid - shown when a role is selected */}
                            {inviteForm.role && !['owner', 'empresa'].includes(inviteForm.role) && (
                                <ModulesGrid>
                                    <div className="modules-grid-header">
                                        <span className="modules-grid-title">
                                            <FiSettings style={{ fontSize: 14 }} />
                                            {t('modules.title')}
                                        </span>
                                        <div className="modules-grid-levels">
                                            <span>{t('levels.read')}</span>
                                            <span>{t('levels.write')}</span>
                                            <span>{t('levels.delete')}</span>
                                        </div>
                                    </div>
                                    {VALID_MODULES.map(moduleName => (
                                        <div key={moduleName} className="modules-grid-row">
                                            <span className="modules-grid-module-name">
                                                {t(`modules.${moduleName}`)}
                                            </span>
                                            <div className="modules-grid-checkboxes">
                                                {['read', 'write', 'delete'].map(level => (
                                                    <div key={level} className="modules-grid-checkbox-wrapper">
                                                        <input
                                                            type="checkbox"
                                                            className="modules-grid-checkbox"
                                                            checked={getModuleLevel(inviteModules, moduleName, level)}
                                                            onChange={(e) => handleModuleCheckbox(inviteModules, setInviteModules, moduleName, level, e.target.checked)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </ModulesGrid>
                            )}

                            {inviteForm.role && ['owner', 'empresa'].includes(inviteForm.role) && (
                                <ModulesGrid>
                                    <div className="modules-grid-bypass">
                                        {t('modules.noModulesNeeded')}
                                    </div>
                                </ModulesGrid>
                            )}
                        </div>

                        <div className="team-modal-footer">
                            <ModalCancelButton
                                onClick={() => setShowInviteModal(false)}
                                disabled={inviteLoading}
                            >
                                {t('invite.cancel')}
                            </ModalCancelButton>
                            <ModalSubmitButton
                                onClick={handleInviteSubmit}
                                disabled={inviteLoading}
                            >
                                {inviteLoading && <div className="team-btn-spinner" />}
                                {t('invite.submit')}
                            </ModalSubmitButton>
                        </div>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* ========== UNIFIED EDIT MODAL (Role + Modules) ========== */}
            {showModulesModal && modulesEditTarget && (
                <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget && !modulesLoading) setShowModulesModal(false); }}>
                    <ModalContent style={{ maxWidth: '560px' }}>
                        <div className="team-modal-header">
                            <h2>{t('actions.editMember', { defaultValue: 'Edit Member' })} - {modulesEditTarget.name}</h2>
                            <button className="team-modal-close" onClick={() => !modulesLoading && setShowModulesModal(false)}>
                                <FiX />
                            </button>
                        </div>

                        <div className="team-modal-body">
                            {/* Role selector */}
                            <FormGroup>
                                <label>{t('invite.role')}</label>
                                <select
                                    value={editRole}
                                    onChange={(e) => handleEditRoleChange(e.target.value)}
                                >
                                    {[modulesEditTarget.role, ...availableRoles.filter(r => r !== modulesEditTarget.role)]
                                        .filter((v, i, a) => a.indexOf(v) === i)
                                        .map(role => (
                                            <option key={role} value={role}>
                                                {getRoleLabel(role)}
                                            </option>
                                        ))
                                    }
                                </select>
                            </FormGroup>

                            {/* Modules grid - only for non-bypass roles */}
                            {!['owner', 'empresa', 'Admin'].includes(editRole) && (
                                <ModulesGrid>
                                    <div className="modules-grid-header">
                                        <span className="modules-grid-title">
                                            <FiSettings style={{ fontSize: 14 }} />
                                            {t('modules.title')}
                                        </span>
                                        <div className="modules-grid-levels">
                                            <span>{t('levels.read')}</span>
                                            <span>{t('levels.write')}</span>
                                            <span>{t('levels.delete')}</span>
                                        </div>
                                    </div>
                                    {VALID_MODULES.map(moduleName => (
                                        <div key={moduleName} className="modules-grid-row">
                                            <span className="modules-grid-module-name">
                                                {t(`modules.${moduleName}`)}
                                            </span>
                                            <div className="modules-grid-checkboxes">
                                                {['read', 'write', 'delete'].map(level => (
                                                    <div key={level} className="modules-grid-checkbox-wrapper">
                                                        <input
                                                            type="checkbox"
                                                            className="modules-grid-checkbox"
                                                            checked={getModuleLevel(editModules, moduleName, level)}
                                                            onChange={(e) => handleModuleCheckbox(editModules, setEditModules, moduleName, level, e.target.checked)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </ModulesGrid>
                            )}
                        </div>

                        <div className="team-modal-footer">
                            <ModalCancelButton
                                onClick={() => setShowModulesModal(false)}
                                disabled={modulesLoading}
                            >
                                {t('invite.cancel')}
                            </ModalCancelButton>
                            <ModalSubmitButton
                                onClick={handleSaveEdit}
                                disabled={modulesLoading}
                            >
                                {modulesLoading && <div className="team-btn-spinner" />}
                                {t('actions.saveButton')}
                            </ModalSubmitButton>
                        </div>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* ========== DELETE CONFIRM ========== */}
            <ConfirmDialog
                title={t('actions.delete', { defaultValue: 'Delete Member' })}
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
            >
                {memberToDelete
                    ? (t('actions.confirmDelete', {
                        defaultValue: `Are you sure you want to permanently delete {{name}}? This action cannot be undone and the member will lose all access.`,
                        name: memberToDelete.name
                      }))
                    : ''
                }
            </ConfirmDialog>
        </PermissionGate>
    );
};

export default TeamManagement;
